import { useCallback, useEffect, useState } from 'react';
import { HubOption, isContinueOption } from 'dorian-shared/types/branch/HubOption';

export function useHubSelection(options: HubOption[]) {
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());

  const regularOptions = options.filter((o) => !isContinueOption(o));

  // Clean up stale selections when options change
  useEffect(() => {
    setSelectedUuids((prev) => {
      const currentUuids = new Set(regularOptions.map((o) => o.uuid));
      const cleaned = new Set<string>();
      prev.forEach((uuid) => {
        if (currentUuids.has(uuid)) cleaned.add(uuid);
      });
      if (cleaned.size === prev.size) return prev; // No change — avoid re-render
      return cleaned;
    });
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleSelect = useCallback((uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedUuids(new Set(regularOptions.map((o) => o.uuid)));
  }, [regularOptions]);

  const handleDeselectAll = useCallback(() => {
    setSelectedUuids(new Set());
  }, []);

  const selectedCount = regularOptions.filter((o) => selectedUuids.has(o.uuid)).length;
  const allSelected = regularOptions.length > 0 && selectedCount === regularOptions.length;

  return {
    selectedUuids,
    selectedCount,
    allSelected,
    handleToggleSelect,
    handleSelectAll,
    handleDeselectAll,
  };
}
